import { Component, HostListener, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpectrumModule, SpectrumModuleService, SpectrumModuleIcon, getModuleIcon } from '../module.service';
import { SPECTRUM_LOCKUP, SPECTRUM_MARK } from '../../brand/spectrum-logo';

/**
 * Module switcher for the dark app sidebar — the same module list and launch
 * as the header switcher, rendered as the sidebar's brand block.
 *
 * The panel is positioned `fixed` from the trigger's box on open: sidebars
 * differ (some clip their content with `overflow: hidden`), and an absolutely
 * positioned panel would be cut off in those.
 */
@Component({
    selector: 'spectrum-sidebar-module-switcher',
    imports: [CommonModule, FormsModule],
    templateUrl: './sidebar-module-switcher.component.html',
    styleUrl: './sidebar-module-switcher.component.scss'
})
export class SpectrumSidebarModuleSwitcherComponent implements OnInit {

    /** Label shown when the current origin doesn't match a module route. */
    @Input() moduleName = '';

    /** Icon-only rail mode — the sidebar is collapsed. */
    @Input() collapsed = false;

    readonly lockup = SPECTRUM_LOCKUP;
    readonly mark = SPECTRUM_MARK;

    private moduleService = inject(SpectrumModuleService);

    modules: SpectrumModule[] = [];
    open = false;
    search = '';

    panelTop = 0;
    panelLeft = 0;
    panelWidth = 0;
    panelMaxHeight = 0;

    ngOnInit(): void {
        this.moduleService.getModules().subscribe(modules => this.modules = modules);
    }

    get currentModule(): SpectrumModule | null {
        return this.moduleService.currentModule(this.modules);
    }

    get label(): string {
        return this.currentModule?.moduleName || this.moduleName || 'Modules';
    }

    get filteredModules(): SpectrumModule[] {
        const q = this.search.trim().toLowerCase();
        if (!q) return this.modules;
        return this.modules.filter(module => module.moduleName.toLowerCase().includes(q));
    }

    isCurrent(module: SpectrumModule): boolean {
        return this.moduleService.isCurrent(module, this.modules);
    }

    moduleIcon(module: SpectrumModule | null): SpectrumModuleIcon {
        return getModuleIcon(module?.moduleName);
    }

    toggle(event: MouseEvent): void {
        event.stopPropagation();
        if (this.open) {
            this.open = false;
            return;
        }
        if (!this.modules.length) return;

        this.position((event.currentTarget as HTMLElement).getBoundingClientRect());
        this.search = '';
        this.open = true;
    }

    /**
     * Sizes and places the panel from the trigger box, matching the header
     * switcher's menu box (34rem wide, 39rem tall at most). Drops the panel
     * below the trigger when there is room, otherwise pins it to the viewport.
     */
    private position(rect: DOMRect): void {
        const gap = 10;
        const margin = 16;
        const width = Math.min(544, window.innerWidth - margin * 2);
        const preferredHeight = Math.min(624, window.innerHeight - margin * 2);
        const spaceBelow = window.innerHeight - rect.bottom - gap - margin;

        if (spaceBelow >= 320) {
            this.panelMaxHeight = Math.min(preferredHeight, spaceBelow);
            this.panelTop = rect.bottom + gap;
        } else {
            this.panelMaxHeight = preferredHeight;
            this.panelTop = Math.max(margin, window.innerHeight - margin - preferredHeight);
        }

        const left = this.collapsed ? rect.right + gap : rect.left;
        this.panelWidth = width;
        this.panelLeft = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
    }

    /**
     * Ctrl/Cmd-click (and middle-click, via `auxclick`) opens the module in a
     * new tab the way a link would; a plain click still replaces this one.
     */
    select(module: SpectrumModule, event?: MouseEvent): void {
        const newTab = this.moduleService.wantsNewTab(event);
        this.open = false;
        this.moduleService.switchModule(module, newTab);
    }

    /** The per-row arrow: always a new tab, and the panel stays open so several can be opened. */
    openInNewTab(module: SpectrumModule, event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.moduleService.switchModule(module, true);
    }

    @HostListener('document:click')
    close(): void {
        this.open = false;
    }

    @HostListener('document:keydown.escape')
    closeOnEscape(): void {
        this.open = false;
    }

    @HostListener('window:resize')
    closeOnResize(): void {
        this.open = false;
    }
}
